import * as React from 'react';
import { Select, SelectProps, Tooltip } from 'antd';
import { SelectValue } from 'antd/lib/select';
import { useDropdownTextSelection, useDropdownOpenState } from '../../hooks';
import { CustomInputLabel, CustomModifiedMarker } from '..';
import { Utils } from '../../misc';
import './CustomSelectMultiple.less';

type CustomProps = {
    label: string;
    labelTooltip?: string;
    required?: boolean;
    mode?: 'multiple' | 'tags';
    topRightNode?: React.ReactNode;
    containerStyle?: React.CSSProperties;
    hasError?: boolean;
    showModifiedMarker?: boolean;
    getContainerRef?: (containerRef: React.RefObject<HTMLDivElement>) => void;
};

export type CustomSelectMultipleProps = CustomProps & Omit<SelectProps<SelectValue>, 'placeholder'>;

const CustomSelectMultiple: React.FC<CustomSelectMultipleProps> = (props: CustomSelectMultipleProps) => {
    const {
        open,
        label,
        labelTooltip,
        required,
        maxTagCount = 'responsive',
        size = 'small',
        mode = 'multiple',
        topRightNode,
        containerStyle,
        hasError,
        showModifiedMarker,
        getContainerRef,
        onPopupScroll,
        onMouseDown,
        onDropdownVisibleChange,
        ...selectProps
    } = props;

    const containerRef = React.useRef<HTMLDivElement>(null);

    const { clearTextSelection, handleTextSelection, renderTextSelectionButton } =
        useDropdownTextSelection(containerRef);

    const { isDropdownOpen, setIsDropdownOpen } = useDropdownOpenState(open);

    React.useEffect(() => {
        if (getContainerRef) {
            getContainerRef(containerRef);
        }
    }, [getContainerRef]);

    const getContainerClassName = () => {
        let className = 'custom-select-multiple-container';

        if (hasError) {
            className += ' has-error';
        }

        return className;
    };

    const getSelectMultipleClassName = () => {
        let className = 'custom-select-multiple';

        if (Utils.hasValue(selectProps.value)) {
            className += ' not-empty';
        }

        return className;
    };

    const renderMaxTagPlaceholder = (omittedValues: { label?: React.ReactNode }[]) => (
        <Tooltip
            overlayStyle={{ maxWidth: 500 }}
            title={
                <div>
                    {omittedValues.map((value, index) => (
                        <div key={index}>{value.label}</div>
                    ))}
                </div>
            }
        >
            <span>+ {omittedValues.length} ...</span>
        </Tooltip>
    );

    return (
        <div
            ref={containerRef}
            className={getContainerClassName()}
            style={containerStyle}
            onContextMenu={event => event.preventDefault()}
        >
            {!!topRightNode && <div className="top-right-node-container">{topRightNode}</div>}

            <Select
                open={isDropdownOpen}
                className={getSelectMultipleClassName()}
                dropdownClassName="custom-select-multiple-dropdown"
                size={size}
                mode={mode}
                maxTagCount={maxTagCount}
                maxTagPlaceholder={renderMaxTagPlaceholder}
                onDropdownVisibleChange={isOpen => {
                    setIsDropdownOpen(isOpen);
                    onDropdownVisibleChange?.(isOpen);
                    clearTextSelection();
                }}
                onMouseDown={event => {
                    onMouseDown?.(event);
                    handleTextSelection(event);
                }}
                onPopupScroll={event => {
                    onPopupScroll?.(event);
                    clearTextSelection();
                }}
                {...selectProps}
            />

            {renderTextSelectionButton()}

            <CustomInputLabel label={label} labelTooltip={labelTooltip} required={required} />

            {showModifiedMarker && <CustomModifiedMarker />}
        </div>
    );
};

export default CustomSelectMultiple;
